// THE MONEY REGISTER (UI rebuild batch 2) — Money movements' default list, on Astra's register: ONE ROW PER ENTRY,
// newest first. Laptop: Party / reference (the name; the short reference and the Dubai time beneath) · Amount · Account ·
// Direction (the kind pill today's screen draws: Money in / Money out / Move …) · a chevron. Phone: a card — the name and
// the chevron, the reference and time, the amount beside the direction, the account. A trade moved two currencies, so
// its Amount and Account seats carry a line per currency (`linesOf`, the fold today's blocks use) — one entry, one row.
// A row opens its receipt (EntrySheet), where the deal and the reference's copy now live. The figures are
// MovementTable's own pieces (MovementFigure, lineAccount), never re-derived here.
//
// THE BALANCE after each line (restored 1 Nov 2026, his "restore most the lost things"): once the Worker sends `legs`, a
// Balance seat beside the Amount — the balance of the desk's account(s) the line moved, in that currency, after it. With
// the account filter naming one account, StatementRegister draws that account's statement instead.
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Register, whenColumn, type RegisterColumn } from '../kit';
import { Figure, KindPill } from '../../Ledger';
import { displayAsset } from '../../../lib/format';
import {
  MovementFigure, lineAccount, linesOf, railWords,
  type MovementLineRow, type StatementLine,
} from '../../MovementTable';
import { movementKindLabel } from '../../../lib/naming';
import { lead, refOnly, rowName, stateWord } from './words';

/** The direction in words, and the state ahead of it when the entry is not live. */
export function Direction({ m }: { m: MovementLineRow }) {
  const s = stateWord(m);
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-2">
      {s && <span className={`text-meta ${m.state === 'reversal' ? 'text-warn' : 'text-tg-hint'}`}>{s}</span>}
      <KindPill label={movementKindLabel(m.kind)} />
    </span>
  );
}

/** The row's door, drawn: the whole row opens the receipt. */
export const Chevron = () => <ChevronRight className="ml-auto h-4 w-4 text-tg-hint" aria-hidden />;

/** The name over its reference and time — the register's first seat, and the phone card's head. */
/** The name and its quiet second line: the reference alone — the When column (table) or the card's time gutter holds the
 *  time. */
export function NameCell({ line, card = false }: { line: StatementLine; card?: boolean }) {
  const live = line.m.state === 'live';
  return (
    <span className="block min-w-0">
      <span className={`line-clamp-2 break-words font-medium ${live ? '' : 'text-tg-hint'}`} title={rowName(line)}>{rowName(line)}</span>
      <span className="mt-1 block truncate text-meta font-normal text-tg-hint">
        {refOnly(line.m)}
      </span>
    </span>
  );
}

/** One currency per line, stacked inside one seat. */
function Stack({ lines, each }: { lines: StatementLine[]; each: (l: StatementLine) => ReactNode }) {
  return <>{lines.map((l) => <div key={l.key} className="min-w-0 truncate">{each(l)}</div>)}</>;
}

const accountOf = (l: StatementLine) => {
  const a = lineAccount(l);
  return a ? railWords(a) : '—';
};

const balanceOf = (l: StatementLine) => (l.running !== null
  ? <span className="whitespace-nowrap"><Figure value={l.running} asset={l.asset} /> <span className="text-tg-hint">{displayAsset(l.asset)}</span></span>
  : <span className="text-tg-hint">—</span>);

/** The register's seats (exported so the kit's table-start rule is pinned against them, not a copy). */
export function movementColumns(showBalance: boolean): RegisterColumn<MovementLineRow>[] {
  return [
    whenColumn<MovementLineRow>((m) => m.at),
    // MONEY IS NEVER CUT: the table starts where these seats and a readable name fit (the kit works it out — cards below);
    // there the figures' seats are FIXED to hold "1,800,000 USDT·TRC" and "36,048,525.59 USDT" whole wherever the table is drawn, the Direction pill keeps 120 px,
    // and the NAME takes what is left — a name is cut with its title, a figure never.
    { key: 'entry', head: 'Party / reference', cell: (m) => <NameCell line={lead(m)} /> },
    // At 1024 (a laptop keeps its table): the figures' seats narrow to what their largest figures need, and the Account seat
    // waits for 1280 — below it the account is a quiet line under the Direction pill (the row's "where", beside its "what").
    // At 1280 the cells pad 16 px a side: the figures' seats grow by that much; their full width only at 1440 — the name keeps
    // about 190 px at 1280.
    { key: 'amount', head: 'Amount', align: 'right', width: 'w-[11.25rem] xl:w-[11.75rem] min-[1440px]:w-[12.5rem]', cell: (m) => <Stack lines={linesOf(m)} each={(l) => <MovementFigure line={l} code />} /> },
    ...(showBalance ? [{ key: 'bal', head: 'Balance', align: 'right' as const, width: 'w-[11.25rem] xl:w-[11.75rem] min-[1440px]:w-[13rem]', cell: (m: MovementLineRow) => <Stack lines={linesOf(m)} each={balanceOf} /> }] : []),
    { key: 'account', head: 'Account', align: 'right', width: 'w-[9rem] min-[1440px]:w-[11rem]', from: 'xl', cell: (m) => <Stack lines={linesOf(m)} each={accountOf} /> },
    { key: 'dir', head: 'Direction', align: 'right', width: 'w-[7.5rem]', cell: (m) => {
      const where = [...new Set(linesOf(m).map(accountOf).filter((a) => a !== '—'))].join(', ');
      return <><Direction m={m} />{where && <span className="mt-1 block truncate text-meta text-tg-hint xl:hidden" title={where}>{where}</span>}</>;
    } },
    { key: 'go', head: '', width: 'w-[1.75rem] xl:w-10', cell: () => <Chevron /> },
  ];
}

export function MovementRegister({ rows, onOpen, showBalance = false }: {
  rows: MovementLineRow[];
  onOpen: (m: MovementLineRow) => void;
  /** The Balance seat — only once the Worker sends `legs` (a column of blanks would read as "no balance"). */
  showBalance?: boolean;
}) {
  const columns = movementColumns(showBalance);
  return (
    <Register
      label="Money movements"
      columns={columns}
      rows={rows}
      rowKey={(m) => m.txn_id}
      onOpen={onOpen}
      card={(m) => {
        const lines = linesOf(m);
        const accounts = lines.map(accountOf).filter((a) => a !== '—').join(', ');
        return {
          title: (
            <>
              {/* The card's title seat is sized to its content; this zero-height line asks for the whole width (a
                  percentage cap is not counted while the seat measures itself), so the amount and the direction below
                  sit at the two edges of the card, as Astra draws them. */}
              <span aria-hidden className="block h-0 w-screen max-w-full" />
              <NameCell line={lead(m)} card />
              <span className="mt-3 flex items-start justify-between gap-3">
                <span className="min-w-0"><Stack lines={lines} each={(l) => <MovementFigure line={l} code />} /></span>
                <Direction m={m} />
              </span>
            </>
          ),
          figure: <Chevron />,
          meta: accounts || undefined,
        };
      }}
    />
  );
}
